import type { WerkcurveOutput, WerkcurveLevel } from '@/lib/interview/types';
import { WERKCURVE_LEVELS } from '@/lib/interview/outputs/werkcurve-levels';

const LEVELS = Object.keys(WERKCURVE_LEVELS) as WerkcurveLevel[];

export function WerkcurveLadder({ level }: { level: WerkcurveLevel }) {
  return (
    <ol className="grid grid-cols-6 gap-1 items-end" aria-label="De werkcurve van L0 tot L5">
      {LEVELS.map((code, i) => {
        const active = code === level;
        return (
          <li
            key={code}
            aria-current={active ? 'step' : undefined}
            className={`rounded-lg px-1.5 sm:px-2 pb-2 flex flex-col justify-end ${
              active ? 'bg-orange-cta text-white' : 'bg-off-black/5 text-off-black/60'
            }`}
            // The curve: flat until the leap from L2 to L3, then it climbs
            style={{ height: `${[44, 48, 52, 72, 92, 112][i]}px` }}
          >
            <span className="text-xs font-medium">{code}</span>
            <span className={`hidden sm:block text-[11px] leading-tight ${active ? 'text-white/90' : 'text-off-black/45'}`}>
              {WERKCURVE_LEVELS[code].name.replace('AI als ', '')}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function WerkcurveTab({ werkcurve: w }: { werkcurve: WerkcurveOutput }) {
  const level = WERKCURVE_LEVELS[w.level];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 border border-off-black/10">
        <p className="text-sm text-off-black/50 mb-1">Waar jullie werk nu zit</p>
        <h2 className="font-display font-light text-3xl text-off-black">
          {w.level} · {level.name}
        </h2>
        <p className="text-off-black/70 mt-2">{level.who}</p>
        <div className="mt-6">
          <WerkcurveLadder level={w.level} />
        </div>
        <p className="text-xs text-off-black/45 mt-4">
          Een inschatting uit één gesprek, zekerheid {w.confidence}. Het gaat om wie het werk doet, niet om welke
          tools jullie hebben. De grote stap is die van L2 naar L3: AI doet dan zelf een taak in plaats van jullie te
          helpen.
        </p>
      </div>

      {w.reasoning.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-off-black/10">
          <h2 className="font-display font-light text-2xl text-off-black mb-4">Waarom we dit denken</h2>
          <ul className="space-y-4">
            {w.reasoning.map((r, i) => (
              <li key={i}>
                <p className="text-off-black/80">{r.point}</p>
                {r.quote && (
                  <blockquote className="mt-1 pl-3 border-l-2 border-orange-cta text-sm text-off-black/55 italic">
                    {r.quote}
                  </blockquote>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {w.not_yet.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-off-black/10">
          <h2 className="font-display font-light text-2xl text-off-black mb-1">Waar AI nog niet zit</h2>
          <p className="text-sm text-off-black/50 mb-4">Hier doen mensen nog al het werk. Vaak zit daar de meeste winst.</p>
          <ul className="space-y-2">
            {w.not_yet.map((n, i) => (
              <li key={i} className="flex gap-2 text-off-black/70">
                <span className="text-orange-cta">&#8226;</span>
                {n}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
